import { useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';

import { useToast } from '~/eds/src/components/hooks/useToast';
import { useRouting } from '~/routing/useRouting';

const TOAST_KEY = 'integration-connection-error';

interface UseIntegrationErrorToastParams {
  isError: boolean;
  isSuccess: boolean;
}

export const useIntegrationErrorToast = ({
  isError,
  isSuccess,
}: UseIntegrationErrorToastParams) => {
  const intl = useIntl();
  const { toast, dismiss } = useToast();
  const { navigate } = useRouting();

  const hasShownErrorToast = useRef(false);
  const dismissRef = useRef(dismiss);
  const navigateRef = useRef(navigate);

  dismissRef.current = dismiss;
  navigateRef.current = navigate;

  useEffect(() => {
    if (isError && !hasShownErrorToast.current) {
      hasShownErrorToast.current = true;
      toast({
        key: TOAST_KEY,
        autoDismiss: false,
        title: intl.formatMessage({
          id: 'IntegrationErrorToast.title',
          defaultMessage: 'Integration API Connection failed',
          description: 'Title for toast when integration API connection fails',
        }),
        message: intl.formatMessage({
          id: 'IntegrationErrorToast.message',
          defaultMessage:
            'Some external data is temporarily unavailable. Try again later.',
          description:
            'Message for toast when integration API connection fails',
        }),
        status: 'danger',
        action: {
          text: intl.formatMessage({
            id: 'IntegrationErrorToast.closeForm',
            defaultMessage: 'Close Form',
            description: 'Button to close the ticket creation form',
          }),
          onClick: () => navigateRef.current('/workflow/tickets'),
        },
      });
    }
  }, [isError, toast, intl]);

  useEffect(() => {
    if (isSuccess && hasShownErrorToast.current) {
      hasShownErrorToast.current = false;
      dismiss(TOAST_KEY);
    }
  }, [isSuccess, dismiss]);

  useEffect(() => {
    return () => {
      dismissRef.current(TOAST_KEY);
    };
  }, []);

  const connectionErrorMessage = isError
    ? intl.formatMessage({
        id: 'IntegrationErrorToast.inlineError',
        defaultMessage: 'Unable to connect right now. Try again later.',
        description:
          'Inline error message shown when integration API connection fails',
      })
    : undefined;

  return { connectionErrorMessage };
};
